// Splits a post the user wrote themselves into the four blocks the generator
// returns, so the editor, image generation, carousels and publishing treat it
// exactly like a generated post.
//   hook     → first line of the first paragraph, max 210 characters
//   hashtags → trailing paragraph(s) made only of #tags, stored without "#"
//   cta      → last remaining paragraph, only if it ends with "?"
//   cuerpo   → everything in between, paragraphs joined with "\n\n"
//              (the same separator the backend uses)

const TAG = /#[\p{L}\p{N}_]+/gu;
const MAX_HOOK = 210; // the editor's hook limit — longer hooks get cut there
const isHashtagLine = (p) => p.replace(TAG, "").trim() === "";

// The hook is the first line, kept under the limit by ending it at the last
// full sentence that fits. Whatever doesn't fit moves to the body — never cut.
function splitHook(paragraph) {
  const newline = paragraph.indexOf("\n");
  const firstLine = newline === -1 ? paragraph : paragraph.slice(0, newline);
  const restOfParagraph = newline === -1 ? "" : paragraph.slice(newline + 1).trim();

  let cut = firstLine.length;
  if (firstLine.length > MAX_HOOK) {
    cut = 0;
    for (const m of firstLine.matchAll(/[.!?…]+["”’)]*(?=\s|$)/g)) {
      const end = m.index + m[0].length;
      if (end > MAX_HOOK) break;
      cut = end;
    }
    // No sentence fits: break between two words instead
    if (!cut) cut = firstLine.lastIndexOf(" ", MAX_HOOK);
    if (cut <= 0) cut = MAX_HOOK;
  }

  const hook = firstLine.slice(0, cut).trim();
  const overflow = [firstLine.slice(cut).trim(), restOfParagraph]
    .filter(Boolean)
    .join("\n");
  return [hook, overflow];
}

export default function splitOwnText(text) {
  const clean = (text || "").replace(/\r\n?/g, "\n").trim();

  // Paragraphs are separated by blank lines. Text pasted without any blank
  // lines falls back to one paragraph per line.
  const separator = /\n\s*\n/.test(clean) ? /\n\s*\n/ : /\n/;
  const paragraphs = clean
    .split(separator)
    .map((p) => p.trim())
    .filter(Boolean);

  const hashtags = [];
  while (
    paragraphs.length > 1 &&
    isHashtagLine(paragraphs[paragraphs.length - 1])
  ) {
    const tags = paragraphs.pop().match(TAG) || [];
    hashtags.unshift(...tags.map((tag) => tag.slice(1)));
  }

  const [hook, hookOverflow] = splitHook(paragraphs.shift() || "");
  if (hookOverflow) paragraphs.unshift(hookOverflow);

  // Only take a CTA if some body is left in front of it.
  const last = paragraphs[paragraphs.length - 1];
  const cta =
    paragraphs.length > 1 && last.endsWith("?") ? paragraphs.pop() : "";

  return { hook, cuerpo: paragraphs.join("\n\n"), cta, hashtags };
}

// The same rules, applied to the editor's lines while the user edits their
// own text. Returns the four blocks plus each line's block ("roles"), so the
// editor can colour every line.
export function splitEditorLines(lines) {
  const roles = lines.map(() => "cuerpo");
  const filled = lines
    .map((line, i) => ({ text: line.trim(), i }))
    .filter((line) => line.text);

  if (!filled.length) {
    return { segments: { hook: "", cuerpo: "", cta: "", hashtags: [] }, roles };
  }

  // Hashtags: the trailing lines made only of #tags
  const hashtags = [];
  while (
    filled.length > 1 &&
    isHashtagLine(filled[filled.length - 1].text)
  ) {
    const { text, i } = filled.pop();
    roles[i] = "hashtags";
    hashtags.unshift(...(text.match(TAG) || []).map((tag) => tag.slice(1)));
  }

  // Hook: the first line with text
  const hookLine = filled.shift();
  roles[hookLine.i] = "hook";

  // CTA: the last remaining line, if it's a question and some body is left
  let cta = "";
  const last = filled[filled.length - 1];
  if (filled.length > 1 && last.text.endsWith("?")) {
    filled.pop();
    roles[last.i] = "cta";
    cta = last.text;
  }

  // Body: every line between, blank lines included
  const cuerpo = filled.length
    ? lines
        .slice(filled[0].i, filled[filled.length - 1].i + 1)
        .map((line) => line.trimEnd())
        .join("\n")
    : "";

  // Blank lines take the block of the line above (except the hook's)
  roles.forEach((role, i) => {
    if (!lines[i].trim() && i > 0 && roles[i - 1] !== "hook") {
      roles[i] = roles[i - 1];
    }
  });

  return { segments: { hook: hookLine.text, cuerpo, cta, hashtags }, roles };
}